import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import { orderApi } from '../../api/order';

// 分页查询订单
export const fetchOrderPage = createAsyncThunk(
  'order/fetchPage',
  async ({ page = 1, pageSize = 10, queryParams = {} }, { rejectWithValue }) => {
    try {
      const response = await orderApi.pageOrders(page, pageSize, queryParams);
      
      // 处理复杂的嵌套数据结构
      const processedData = {
        records: (response.data?.records || []).map(item => {
          const order = item.order || {};
          const orderItems = item.orderItems || [];
          const store = item.store || {};
          const user = item.user || {};
          
     
          
          const processedOrder = {
            id: order.id,
            order_no: order.orderNo,
            user_id: order.userId,
            store_id: order.storeId,
            total_amount: order.totalAmount ?? 0,
            pay_amount: order.payAmount ?? 0,
            
            // 主状态映射
            main_status: order.mainStatus, // 使用 main_status 而不是 status
            status: order.mainStatus, // 保留 status 作为兼容
            
            // 新增的状态字段
            pay_status: order.payStatus,
            ship_status: order.shipStatus,
            close_status: order.closeStatus,
            refund_status: order.refundStatus,
            receive_status: order.receiveStatus,
            
            // 是否已支付快检费
            is_fee_paid: order.isFeePaid ?? false,
            
            payment_method: '', // 从原数据结构中没有直接对应的字段
            address_id: order.addressId,
            remark: '', // 原数据结构中没有备注字段
            create_time: formatCreateTime(order.createTime),
            pay_time: order.payTime ? formatCreateTime(order.payTime) : null,
            order_items: orderItems.map(item => ({
              id: item.id,
              goods_id: item.goodsId,
              goods_name: item.goodsName,
              goods_price: item.goodsPrice,
              goods_quantity: item.goodsQuantity,
              total_price: item.totalPrice
            })),
            // 额外的用户和店铺信息
            user_nickname: user.nickname,
            store_name: store.storeName,
            // 平台发货标识
            isPlatformShipment: item.isPlatformShipment ?? false
          };
          
          console.log('处理后的订单对象:', processedOrder);
          
          return processedOrder;
        }),
        total: response.data?.total ?? 0,
        current: response.data?.current ?? page,
        size: response.data?.size ?? pageSize,
        pages: response.data?.pages ?? 1
      };

      return processedData;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取订单列表失败' });
    }
  }
);

// 创建订单
export const createOrder = createAsyncThunk(
  'order/create',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await orderApi.createOrder(orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '创建订单失败' });
    }
  }
);

// 更新订单
export const updateOrder = createAsyncThunk(
  'order/update',
  async ({ orderId, orderData }, { rejectWithValue }) => {
    try {
      const response = await orderApi.updateOrder(orderId, orderData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '更新订单失败' });
    }
  }
);

// 删除订单
export const deleteOrder = createAsyncThunk(
  'order/delete',
  async (orderId, { rejectWithValue, dispatch }) => {
    try {
      const response = await orderApi.deleteOrder(orderId);
      message.success('删除成功');
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 获取平台收货记录
export const fetchPlatformReceiveRecords = createAsyncThunk(
  'order/fetchPlatformReceiveRecords',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderApi.getPlatformReceiveRecords(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取平台收货记录失败' });
    }
  }
);

// 获取快检费记录
export const fetchPlatformFeeOrder = createAsyncThunk(
  'order/fetchPlatformFeeOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await orderApi.getPlatformFeeOrder(orderId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: '获取快检费记录失败' });
    }
  }
);

const orderSlice = createSlice({
  name: 'order',
  initialState: {
    orders: [],
    total: 0,
    loading: false,
    error: null,
    currentPage: 1,
    pageSize: 10,
    platformReceiveRecords: [],
    platformReceiveRecordsLoading: false,
    platformFeeOrder: null,
    platformFeeOrderLoading: false
  },
  reducers: {},
  extraReducers: (builder) => {
    // 分页查询订单
    builder.addCase(fetchOrderPage.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchOrderPage.fulfilled, (state, action) => {
      state.loading = false;
      state.orders = action.payload.records;
      state.total = action.payload.total;
      state.currentPage = action.payload.current;
      state.pageSize = action.payload.size;
    });
    builder.addCase(fetchOrderPage.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // 创建订单
    builder.addCase(createOrder.fulfilled, (state, action) => {
      // 可以在这里更新状态，例如重新获取列表
    });

    // 更新订单
    builder.addCase(updateOrder.fulfilled, (state, action) => {
      // 可以在这里更新状态，例如重新获取列表
    });

    // 删除订单
    builder.addCase(deleteOrder.fulfilled, (state, action) => {
      // 可以在这里更新状态，例如重新获取列表
    });

    // 获取平台收货记录
    builder.addCase(fetchPlatformReceiveRecords.pending, (state) => {
      state.platformReceiveRecordsLoading = true;
    });
    builder.addCase(fetchPlatformReceiveRecords.fulfilled, (state, action) => {
      state.platformReceiveRecordsLoading = false;
      state.platformReceiveRecords = action.payload;
    });
    builder.addCase(fetchPlatformReceiveRecords.rejected, (state, action) => {
      state.platformReceiveRecordsLoading = false;
      state.error = action.payload;
    });

    // 获取快检费记录
    builder.addCase(fetchPlatformFeeOrder.pending, (state) => {
      state.platformFeeOrderLoading = true;
    });
    builder.addCase(fetchPlatformFeeOrder.fulfilled, (state, action) => {
      state.platformFeeOrderLoading = false;
      state.platformFeeOrder = action.payload;
    });
    builder.addCase(fetchPlatformFeeOrder.rejected, (state, action) => {
      state.platformFeeOrderLoading = false;
      state.error = action.payload;
    });
  }
});

export default orderSlice.reducer; 

// 辅助函数：将后端的时间数组转换为可读的日期字符串
function formatCreateTime(timeArray) {
  if (!Array.isArray(timeArray) || timeArray.length < 6) {
    return '';
  }
  // timeArray: [2025, 10, 4, 10, 12, 21]
  // 转换为 'YYYY-MM-DD HH:mm:ss' 格式
  return `${timeArray[0]}-${
    String(timeArray[1]).padStart(2, '0')
  }-${
    String(timeArray[2]).padStart(2, '0')
  } ${
    String(timeArray[3]).padStart(2, '0')
  }:${
    String(timeArray[4]).padStart(2, '0')
  }:${
    String(timeArray[5]).padStart(2, '0')
  }`;
} 